import { ReactNode } from 'react';
import { List, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { InlineStatus } from 'components/InlineStatus';
import { useReporterNotes } from 'features/reporterNotes/hooks/useReporterNotes';
import {
  ReporterNote,
  ReporterNoteToggle,
} from 'features/reporterNotes/reporterNotes.types';
import { isReporterNoteArchived } from 'features/reporterNotes/reporterNotes.utils';
import { ReporterNoteItem } from './ReporterNoteItem';

type ReporterNotesListProps = {
  geoEventId?: number;
  pendingToggles?: ReporterNoteToggle[];
  renderNoteAction?: (note: ReporterNote) => ReactNode;
  disableGutters?: boolean;
};

export const ReporterNotesList = (
  props: ReporterNotesListProps,
): JSX.Element => {
  const {
    geoEventId,
    pendingToggles = [],
    renderNoteAction,
    disableGutters = false,
  } = props;
  const { t } = useTranslation();
  const { reporterNotes, isLoading, isError } = useReporterNotes({
    geoEventId,
  });
  const gutter = disableGutters ? 0 : 2;

  const header = (
    <Typography
      variant="watchduty-section-header"
      sx={disableGutters ? { p: 0 } : undefined}
    >
      {t('reporterNotes.title')}
    </Typography>
  );

  if (isLoading || isError) {
    return (
      <>
        {header}
        <InlineStatus
          status={isLoading ? 'loading' : 'error'}
          errorMessage={t('reporterNotes.error')}
          fullWidth
        />
      </>
    );
  }

  if (reporterNotes.length === 0) {
    return (
      <>
        {header}
        <Typography color="textSecondary" sx={{ py: 2, px: gutter }}>
          {t('reporterNotes.empty')}
        </Typography>
      </>
    );
  }

  return (
    <>
      {header}
      <List disablePadding>
        {reporterNotes.map((note) => (
          <ReporterNoteItem
            key={note.id}
            note={{
              ...note,
              isArchived: isReporterNoteArchived(note, pendingToggles),
            }}
            action={renderNoteAction?.(note)}
            disableGutters={disableGutters}
          />
        ))}
      </List>
    </>
  );
};
